import { createFileRoute, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { AppIcon } from '../../../components/ui/icon'
import {
    mdiFolderPlus,
    mdiPlus,
    mdiRefresh,
    mdiFileTree,
    mdiFileDocumentEdit,
    mdiFolderOutline,
    mdiFolderMultiple,
    mdiFolderOpen,
    mdiDownload,
    mdiUpload,
} from '@mdi/js'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu'
import { SearchableSelect } from '../../../components/SearchableSelect'
import { HierarchyTree } from '../../../components/hierarchy/HierarchyTree'
import type { HierarchyMove, HierarchyTreeNode } from '../../../components/hierarchy/HierarchyTree'
import { computeOutlineNumbers } from '../../../components/hierarchy/outline-numbering'
import {
    FolderDefinitionEditor,
    NONE_OPTION,
    subtreeUids,
} from '../../../components/folder-layouts/FolderDefinitionEditor'
import type { FolderUpdatePatch } from '../../../components/folder-layouts/FolderDefinitionEditor'
import {
    documentTypeDisplayName,
    folderDisplayName,
} from '../../../components/folder-layouts/display-names'
import { FolderLayoutImportDialog } from '../../../components/folder-layouts/FolderLayoutImportDialog'
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs'
import { useLocale } from '../../../contexts/LocaleContext'
import { useLiveSubscription, useScopeLive } from '../../../contexts/LiveEventsContext'
import { useDynamicRefresh } from '../../../hooks/useDynamicRefresh'
import { apiClient } from '../../../api/client'
import type { components } from '../../../api/schema'
import { cn } from '../../../lib/utils'
import { Can, useCan } from '../../../contexts/PermissionsContext'
import { PageHeader } from '../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../lib/section-icons'

const folderLayoutSearchSchema = z.object({
    folder_uid: z.string().optional(),
    /**
     * Document type the editor should open focused on (spec 2026-08-01-20 §3).
     *
     * Set by the "Propose a folder" affordance of the asset documents tab, for
     * a document whose type no folder of this layout claims. The editor then
     * either selects the folder that already owns the type, or invites the user
     * to create one with the type preselected.
     */
    document_type_uid: z.string().optional(),
})

export const Route = createFileRoute('/$org/folder-layouts/$layoutUid')({
    component: DocumentFoldersPage,
    validateSearch: folderLayoutSearchSchema,
})

type FolderDefinition = components['schemas']['FolderDefinition']
type CreateFolderDefinitionRequest = components['schemas']['CreateFolderDefinitionRequest']
type UpdateFolderDefinitionRequest = components['schemas']['UpdateFolderDefinitionRequest']
type DocumentFolderLayout = components['schemas']['DocumentFolderLayout']
type DocumentType = components['schemas']['DocumentType']

function errorMessage(error: unknown): string {
    if (!error) return ''
    if (typeof error === 'string') return error
    if (typeof error === 'object') {
        const candidate = error as { message?: string; error?: string; code?: string }
        return candidate.message || candidate.error || candidate.code || JSON.stringify(error)
    }
    return String(error)
}

function DocumentFoldersPage() {
    const { org, layoutUid } = useParams({ from: '/$org/folder-layouts/$layoutUid' })
    const navigate = useNavigate({ from: '/$org/folder-layouts/$layoutUid' })
    const search = useSearch({ from: '/$org/folder-layouts/$layoutUid' })
    const { t } = useTranslation('folders')
    // Folder / layout / document-type names are `{en, fr}` maps: every label
    // below is resolved against the active locale, which is therefore a
    // dependency of each memo so a language switch re-labels the tree live.
    const { locale } = useLocale()
    const queryClient = useQueryClient()
    // A folder layout is org configuration. Without `configuration.write` the
    // tree and the folder detail stay fully readable; every affordance that
    // creates, reorders or deletes a folder disappears
    // (spec 2026-09-04-02 §2 "Gating").
    const canWrite = useCan('configuration.write')

    const selectedFolderUid = search.folder_uid || null
    const focusedTypeUid = search.document_type_uid || null

    const [isEditingTree, setIsEditingTree] = useState(false)
    const [mobilePane, setMobilePane] = useState<'tree' | 'detail'>('tree')
    const [pageError, setPageError] = useState<string | null>(null)
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
    const [isExporting, setIsExporting] = useState(false)
    const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
    const [importNotice, setImportNotice] = useState<string | null>(null)
    const [newFolder, setNewFolder] = useState({
        name: { en: '', fr: '' },
        documentTypeUid: '',
        parentUid: '',
    })

    const foldersQueryKey = useMemo(() => ['documentFolders', org, layoutUid], [org, layoutUid])

    const { data: layout, refetch: refetchLayout } = useQuery({
        queryKey: ['documentLayout', org, layoutUid],
        queryFn: async (): Promise<DocumentFolderLayout> => {
            const response = await apiClient.GET(
                '/api/v1/organizations/{orgCode}/document-folders-layouts/{layoutUid}',
                { params: { path: { orgCode: org, layoutUid } } }
            )
            if (response.error) throw new Error(errorMessage(response.error) || 'Failed to load layout')
            return response.data as DocumentFolderLayout
        },
    })

    const {
        data: folders = [],
        isLoading: isFoldersLoading,
        error: foldersError,
        refetch: refetchFolders,
    } = useQuery({
        queryKey: foldersQueryKey,
        queryFn: async (): Promise<FolderDefinition[]> => {
            const response = await apiClient.GET(
                '/api/v1/organizations/{orgCode}/document-folders-layouts/{layoutUid}/folders',
                { params: { path: { orgCode: org, layoutUid } } }
            )
            if (response.error) throw new Error(errorMessage(response.error) || 'Failed to load folders')
            return (response.data as { folders?: FolderDefinition[] })?.folders || []
        },
    })

    const { data: documentTypes = [] } = useQuery({
        queryKey: ['documentTypes', org],
        queryFn: async (): Promise<DocumentType[]> => {
            const response = await apiClient.GET('/api/v1/organizations/{orgCode}/document-types', {
                params: { path: { orgCode: org }, query: { limit: 1000 } },
            })
            if (response.error) throw new Error(errorMessage(response.error) || 'Failed to load document types')
            return (response.data as { data?: DocumentType[] })?.data || []
        },
    })

    // Export comes in two flavours, and they are not interchangeable:
    //  - JSON is the system envelope — portable across orgs/environments, all
    //    cross-references by code, directly re-importable,
    //  - CSV is the customer-facing spreadsheet projection (one row per folder,
    //    hierarchy in the `path` column) that a syndic can open in Excel.
    const downloadExport = useCallback(async (format: 'json' | 'csv') => {
        setIsExporting(true)
        setPageError(null)
        try {
            const response = await apiClient.GET(
                '/api/v1/organizations/{orgCode}/document-folders-layouts/{layoutUid}/export',
                {
                    params: { path: { orgCode: org, layoutUid }, query: { format } },
                    // CSV is read as a blob, not text: `Response.text()` strips
                    // the leading BOM, and Excel needs that BOM to open the file
                    // as UTF-8.
                    ...(format === 'csv' ? { parseAs: 'blob' as const } : {}),
                }
            )
            if (response.error) throw new Error(errorMessage(response.error) || 'Failed to export layout')

            const isCSV = format === 'csv'
            const blob = isCSV
                ? (response.data as unknown as Blob)
                : new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `folder-layout-${layout?.code || layoutUid}.${format}`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            setPageError(t('export.error', { message: errorMessage(error) }))
        } finally {
            setIsExporting(false)
        }
    }, [org, layoutUid, layout?.code, t])

    useBreadcrumbs([
        { title: t('common:nav.home'), path: `/${org}` },
        { title: t('breadcrumb'), path: `/${org}/folder-layouts` },
        { title: layout ? folderDisplayName(layout, locale) : 'Layout' },
    ])

    // Live updates: a write to document_folders or document_folder_layouts
    // dirties the `folder-layouts` collection scope (see docs/apis/events.md),
    // whose invalidation map covers this page's manual query keys — so an edit
    // made in another tab lands here within ~1-2s with no user action. Only
    // the COLLECTION scope is subscribed: a folder-row hint carries the
    // folder's own uid, which would never match a layout-uid detail scope.
    useLiveSubscription({ entity: 'folder-layouts' })
    const isLayoutScopeLive = useScopeLive({ entity: 'folder-layouts' })

    // Polling safety net, same shape as the asset detail page: neither query
    // above sets a refetchInterval, so without this the page would only ever
    // be as fresh as the socket. While live, the interval is pinned to a lazy
    // 5 minutes (a pure backstop); the moment the socket drops, the adaptive
    // backoff becomes the real freshness mechanism again. The Refresh button
    // stays as reassurance + manual override.
    const foldersDynamicRefresh = useDynamicRefresh({
        enabled: true,
        defaultInterval: 5000,
        live: isLayoutScopeLive,
    })
    useEffect(() => {
        if (foldersDynamicRefresh.timeRemaining !== 0) return
        void refetchFolders()
        void refetchLayout()
    }, [foldersDynamicRefresh.timeRemaining, refetchFolders, refetchLayout])

    const invalidateFolders = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: foldersQueryKey })
    }, [queryClient, foldersQueryKey])

    const selectFolder = useCallback(
        (uid: string | undefined) => {
            navigate({ search: prev => ({ ...prev, folder_uid: uid }) })
            if (uid) setMobilePane('detail')
        },
        [navigate]
    )

    // Raw PATCH used by the drag & drop handler, which may fire a re-parent
    // write before the reorder and only wants a single invalidation at the end.
    const patchFolder = useCallback(
        async (uid: string, data: UpdateFolderDefinitionRequest) => {
            const response = await apiClient.PATCH(
                '/api/v1/organizations/{orgCode}/document-folders-layouts/{layoutUid}/folders/{folderUid}',
                { params: { path: { orgCode: org, layoutUid, folderUid: uid } }, body: data }
            )
            if (response.error) throw new Error(errorMessage(response.error) || 'Failed to update folder')
            return response.data
        },
        [org, layoutUid]
    )

    // One atomic write for a whole sibling set. The previous implementation
    // PATCHed each sibling's `ordering` in a loop, comparing against a `folders`
    // snapshot the server was renumbering underneath it — which produced
    // colliding values and let row creation time decide the final order.
    const reorderFolders = useCallback(
        async (parentUid: string | null, orderedUids: string[]) => {
            const response = await apiClient.PUT(
                '/api/v1/organizations/{orgCode}/document-folders-layouts/{layoutUid}/folders/reorder',
                {
                    params: { path: { orgCode: org, layoutUid } },
                    body: { parent_folder_uid: parentUid, ordered_folder_uids: orderedUids },
                }
            )
            if (response.error) throw new Error(errorMessage(response.error) || 'Failed to reorder folders')
            return response.data
        },
        [org, layoutUid]
    )

    const createFolderMutation = useMutation({
        mutationFn: async (data: CreateFolderDefinitionRequest) => {
            const response = await apiClient.POST(
                '/api/v1/organizations/{orgCode}/document-folders-layouts/{layoutUid}/folders',
                { params: { path: { orgCode: org, layoutUid } }, body: data }
            )
            if (response.error) throw new Error(errorMessage(response.error) || 'Failed to create folder')
            return response.data as FolderDefinition
        },
        onSuccess: created => {
            invalidateFolders()
            setIsCreateDialogOpen(false)
            setNewFolder({ name: { en: '', fr: '' }, documentTypeUid: '', parentUid: '' })
            if (created?.uid) selectFolder(created.uid)
        },
        onError: error => setPageError(t('folders.editor.create_error') + ' ' + error.message),
    })

    const updateFolderMutation = useMutation({
        mutationFn: ({ uid, data }: { uid: string; data: UpdateFolderDefinitionRequest }) =>
            patchFolder(uid, data),
        onSuccess: () => invalidateFolders(),
    })

    const deleteFolderMutation = useMutation({
        mutationFn: async (uid: string) => {
            const response = await apiClient.DELETE(
                '/api/v1/organizations/{orgCode}/document-folders-layouts/{layoutUid}/folders/{folderUid}',
                { params: { path: { orgCode: org, layoutUid, folderUid: uid } } }
            )
            if (response.error) throw new Error(errorMessage(response.error) || 'Failed to delete folder')
            return response.data
        },
        onSuccess: () => {
            invalidateFolders()
            selectFolder(undefined)
            setMobilePane('tree')
        },
    })

    const documentTypeName = useCallback(
        (uid: string) => {
            const dt = documentTypes.find(d => d.uid === uid)
            return dt ? documentTypeDisplayName(dt, locale) : undefined
        },
        [documentTypes, locale]
    )

    const treeNodes: HierarchyTreeNode[] = useMemo(
        () =>
            folders.map(folder => ({
                uid: folder.uid,
                label: folderDisplayName(folder, locale),
                parentUid: folder.parent_folder_uid || null,
                ordering: folder.ordering ?? 0,
                badge: folder.document_type_uid ? documentTypeName(folder.document_type_uid) : undefined,
            })),
        [folders, documentTypeName, locale]
    )

    const selectedFolder = useMemo(
        () => folders.find(f => f.uid === selectedFolderUid) || null,
        [folders, selectedFolderUid]
    )

    // Same helper as the tree, so the detail pane shows the very same number.
    const outlineNumbers = useMemo(() => computeOutlineNumbers(treeNodes, true), [treeNodes])

    const handleMove = useCallback(
        async (move: HierarchyMove) => {
            setPageError(null)
            const currentParentUid = folders.find(f => f.uid === move.uid)?.parent_folder_uid ?? null
            try {
                if (currentParentUid !== move.parentUid) {
                    // Re-parent first so the destination's child set matches
                    // `move.siblingOrder` exactly — the reorder endpoint rejects
                    // a list that is not precisely that parent's children.
                    await patchFolder(move.uid, { parent_folder_uid: move.parentUid })
                    // No reorder call for the source parent: the re-parent PATCH
                    // already re-densifies the sibling set the folder left, in
                    // the same transaction. Sending one from here would have to
                    // enumerate that set from the stale `folders` snapshot and
                    // would fail validation whenever the snapshot lags.
                }
                await reorderFolders(move.parentUid, move.siblingOrder)
            } catch (e) {
                setPageError(t('folders.editor.move_error') + ' ' + (e instanceof Error ? e.message : ''))
            } finally {
                invalidateFolders()
            }
        },
        [folders, patchFolder, reorderFolders, invalidateFolders, t]
    )

    // One inline field commit = one partial PATCH. Absent fields are left
    // untouched by the endpoint (tri-state, see UpdateFolderDefinitionRequest),
    // so no read-modify-write of the other fields is needed here.
    const handleUpdate = useCallback(
        async (data: FolderUpdatePatch) => {
            setPageError(null)
            await updateFolderMutation.mutateAsync({ uid: selectedFolder!.uid, data })
        },
        [selectedFolder, updateFolderMutation]
    )

    const handleDelete = useCallback(async () => {
        setPageError(null)
        await deleteFolderMutation.mutateAsync(selectedFolder!.uid)
    }, [selectedFolder, deleteFolderMutation])

    const openCreateDialog = useCallback((parentUid: string | null, documentTypeUid = '') => {
        // Belt and braces: the affordances are hidden, the dialog stays shut.
        if (!canWrite) return
        setNewFolder({ name: { en: '', fr: '' }, documentTypeUid, parentUid: parentUid || '' })
        setIsCreateDialogOpen(true)
    }, [canWrite])

    /**
     * `?document_type_uid=` focus mode (spec 2026-08-01-20 §3): a document on an
     * asset carries a type this layout files nowhere. If some folder already
     * claims the type, jump straight to it (the caller's view of the layout was
     * simply stale); otherwise the banner below invites creating one.
     */
    const folderClaimingFocusedType = useMemo(
        () => (focusedTypeUid ? folders.find(f => f.document_type_uid === focusedTypeUid) ?? null : null),
        [folders, focusedTypeUid]
    )
    const focusedTypeName = focusedTypeUid ? documentTypeName(focusedTypeUid) : undefined

    useEffect(() => {
        if (!folderClaimingFocusedType) return
        if (selectedFolderUid) return
        selectFolder(folderClaimingFocusedType.uid)
    }, [folderClaimingFocusedType, selectedFolderUid, selectFolder])

    const createParentOptions = useMemo(
        () => [
            { value: NONE_OPTION, label: t('folders.no_parent') },
            ...folders
                .map(f => ({ value: f.uid, label: folderDisplayName(f, locale) }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        ],
        [folders, locale, t]
    )

    const createDocumentTypeOptions = useMemo(() => {
        const claimed = new Set(
            folders.filter(f => f.document_type_uid).map(f => f.document_type_uid as string)
        )
        return [
            { value: NONE_OPTION, label: t('folders.no_document_type') },
            ...documentTypes
                .filter(dt => !claimed.has(dt.uid))
                .map(dt => ({ value: dt.uid, label: documentTypeDisplayName(dt, locale) }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        ]
    }, [folders, documentTypes, locale, t])

    const submitCreate = () => {
        // `ordering` is deliberately omitted: the server appends the folder last
        // among its siblings and owns the dense 0..n-1 numbering. Computing it
        // here from a possibly stale `folders` snapshot is what used to produce
        // colliding values.
        createFolderMutation.mutate({
            name: newFolder.name,
            parent_folder_uid: newFolder.parentUid || null,
            document_type_uid: newFolder.documentTypeUid || null,
        })
    }

    if (foldersError) {
        return (
            <div className="p-6">
                <div className="text-center">
                    <p className="text-destructive">
                        {t('folders.error_loading', { message: foldersError.message })}
                    </p>
                    <Button onClick={() => refetchFolders()} className="mt-4" data-testid="folders-retry">
                        <AppIcon path={mdiRefresh} size="md" className="mr-2" />
                        {t('retry_button')}
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className="flex h-screen flex-col">
            {/* Header */}
            <div className="border-b p-4">
                <PageHeader
                    icon={SECTION_ICONS.folderLayouts}
                    title={layout ? folderDisplayName(layout, locale) : t('page_title')}
                    description={
                        <span className="hidden md:block">{t('folders.page_description')}</span>
                    }
                    actions={
                    <>
                        <Button
                            onClick={() => {
                                void refetchFolders()
                                void refetchLayout()
                            }}
                            variant="outline"
                            aria-label={t('refresh_button')}
                            title={t('refresh_button')}
                            data-testid="refresh-folders"
                        >
                            <AppIcon path={mdiRefresh} size="sm" className="md:mr-1" />
                            <span className="hidden md:inline">{t('refresh_button')}</span>
                        </Button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="outline"
                                    disabled={isExporting}
                                    aria-label={t('export.button')}
                                    title={t('export.button')}
                                    data-testid="export-layout-button"
                                >
                                    <AppIcon path={mdiDownload} size="sm" className="md:mr-1" />
                                    <span className="hidden md:inline">{t('export.button')}</span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                    onSelect={() => { void downloadExport('json') }}
                                    data-testid="export-layout-json"
                                >
                                    {t('export.format_json')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onSelect={() => { void downloadExport('csv') }}
                                    data-testid="export-layout-csv"
                                >
                                    {t('export.format_csv')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        {/* Import targets THIS layout: the dialog opens with the
                            target pre-locked, so no layout code has to be typed. */}
                        <Can perm="configuration.write">
                            <Button
                                variant="outline"
                                onClick={() => setIsImportDialogOpen(true)}
                                disabled={!layout?.code}
                                aria-label={t('import.button')}
                                title={t('import.button')}
                                data-testid="import-layout-button"
                            >
                                <AppIcon path={mdiUpload} size="sm" className="md:mr-1" />
                                <span className="hidden md:inline">{t('import.button')}</span>
                            </Button>
                        </Can>
                        <Can perm="configuration.write">
                            <Button
                                onClick={() => openCreateDialog(null)}
                                aria-label={t('folders.add_folder_button')}
                                title={t('folders.add_folder_button')}
                                data-testid="create-folder-button"
                            >
                                <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
                                <span className="hidden md:inline">{t('folders.add_folder_button')}</span>
                            </Button>
                        </Can>
                    </>
                    }
                />

                {pageError && (
                    <div
                        className="mt-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive"
                        role="status"
                        data-testid="folders-page-error"
                    >
                        {pageError}
                    </div>
                )}

                {importNotice && (
                    <div
                        className="mt-3 rounded-lg border border-border bg-muted/50 px-4 py-2 text-sm"
                        role="status"
                        data-testid="import-layout-notice"
                    >
                        {importNotice}
                    </div>
                )}

                {/* Arrived from an asset's unclassified document: no folder of
                    this layout claims that document type yet. */}
                {focusedTypeUid && !folderClaimingFocusedType && (
                    <div
                        className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-muted/50 px-4 py-2 text-sm"
                        role="status"
                        data-testid="folders-focused-type-banner"
                    >
                        <span>
                            {t('folders.focused_type.unclaimed', {
                                type: focusedTypeName || focusedTypeUid,
                            })}
                        </span>
                        <Can perm="configuration.write">
                            <Button
                                size="sm"
                                onClick={() => openCreateDialog(selectedFolderUid, focusedTypeUid)}
                                data-testid="folders-focused-type-create"
                            >
                                <AppIcon path={mdiFolderPlus} size="sm" className="mr-1" />
                                {t('folders.focused_type.create_button')}
                            </Button>
                        </Can>
                    </div>
                )}

                {focusedTypeUid && folderClaimingFocusedType && (
                    <div
                        className="mt-3 rounded-lg border border-border bg-muted/50 px-4 py-2 text-sm"
                        role="status"
                        data-testid="folders-focused-type-banner"
                    >
                        {t('folders.focused_type.claimed', {
                            type: focusedTypeName || focusedTypeUid,
                            folder: folderDisplayName(folderClaimingFocusedType, locale),
                        })}
                    </div>
                )}

                {/* Mobile pane switch — the master-detail split collapses below md. */}
                <div className="mt-3 flex gap-2 md:hidden" data-testid="folders-pane-switch">
                    <Button
                        variant={mobilePane === 'tree' ? 'default' : 'outline'}
                        size="sm"
                        className="flex-1"
                        onClick={() => setMobilePane('tree')}
                        data-testid="folders-pane-tree"
                    >
                        <AppIcon path={mdiFileTree} size="sm" className="mr-1" />
                        {t('folders.view.tree')}
                    </Button>
                    <Button
                        variant={mobilePane === 'detail' ? 'default' : 'outline'}
                        size="sm"
                        className="flex-1"
                        onClick={() => setMobilePane('detail')}
                        data-testid="folders-pane-detail"
                    >
                        <AppIcon path={mdiFileDocumentEdit} size="sm" className="mr-1" />
                        {t('folders.view.details')}
                    </Button>
                </div>
            </div>

            {/* Master-detail */}
            <div className="flex min-h-0 flex-1 overflow-hidden">
                <div
                    className={cn(
                        'min-h-0 w-full overflow-hidden border-r md:block md:w-1/3',
                        mobilePane === 'tree' ? 'block' : 'hidden'
                    )}
                >
                    {isFoldersLoading ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                            {t('loading_folders')}
                        </div>
                    ) : (
                        <HierarchyTree
                            className="h-full"
                            title={t('folders.tree.title', { count: folders.length })}
                            nodes={treeNodes}
                            selectedUid={selectedFolderUid}
                            onSelect={selectFolder}
                            editable={canWrite}
                            editing={isEditingTree}
                            onEditingChange={setIsEditingTree}
                            orderable={canWrite}
                            onMove={canWrite ? handleMove : undefined}
                            onCreateChild={canWrite ? openCreateDialog : undefined}
                            defaultExpanded="all"
                            numbered
                            // Every node of a folder layout IS a folder, so the
                            // shared tree's file-shaped leaf default would be a
                            // lie. A leaf gets the outline folder (empty), a
                            // collapsed parent the stacked folders (there is
                            // more inside), an expanded one the open folder.
                            icons={{
                                leaf: mdiFolderOutline,
                                collapsed: mdiFolderMultiple,
                                expanded: mdiFolderOpen,
                            }}
                            testIdPrefix="folders"
                            labels={{
                                edit: t('folders.tree.edit_mode'),
                                done: t('folders.tree.edit_mode_done'),
                                root: t('folders.root_folder'),
                                dropToRoot: t('folders.drop_to_root'),
                                empty: t('folders.tree.empty'),
                                addChild: t('folders.tree.add_child'),
                            }}
                            headerActions={
                                canWrite ? (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => openCreateDialog(selectedFolderUid)}
                                        aria-label={t('folders.tree.add_child')}
                                        title={t('folders.tree.add_child')}
                                        data-testid="folders-tree-add"
                                    >
                                        <AppIcon path={mdiFolderPlus} size="sm" />
                                    </Button>
                                ) : undefined
                            }
                        />
                    )}
                </div>

                <div
                    className={cn(
                        'min-h-0 w-full flex-1 overflow-y-auto md:block',
                        mobilePane === 'detail' ? 'block' : 'hidden'
                    )}
                >
                    <FolderDefinitionEditor
                        folder={selectedFolder}
                        number={selectedFolder ? outlineNumbers.get(selectedFolder.uid) : undefined}
                        allFolders={folders}
                        documentTypes={documentTypes}
                        onUpdate={handleUpdate}
                        onDelete={handleDelete}
                        onSelectFolder={selectFolder}
                        isDeleting={deleteFolderMutation.isPending}
                    />
                </div>
            </div>

            {/* Create dialog */}
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent data-testid="create-folder-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('folders.create_dialog.title')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 p-6 pt-2">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <Label htmlFor="new-name-en">🇺🇸 {t('folders.create_dialog.name_en_label')}</Label>
                                <Input
                                    id="new-name-en"
                                    placeholder="Contracts"
                                    value={newFolder.name.en}
                                    onChange={e =>
                                        setNewFolder(prev => ({ ...prev, name: { ...prev.name, en: e.target.value } }))
                                    }
                                    data-testid="create-folder-name-en"
                                />
                            </div>
                            <div>
                                <Label htmlFor="new-name-fr">🇫🇷 {t('folders.create_dialog.name_fr_label')}</Label>
                                <Input
                                    id="new-name-fr"
                                    placeholder="Contrats"
                                    value={newFolder.name.fr}
                                    onChange={e =>
                                        setNewFolder(prev => ({ ...prev, name: { ...prev.name, fr: e.target.value } }))
                                    }
                                    data-testid="create-folder-name-fr"
                                />
                            </div>
                        </div>

                        <div>
                            <Label>{t('folders.create_dialog.parent_label')}</Label>
                            <SearchableSelect
                                options={createParentOptions}
                                value={newFolder.parentUid || NONE_OPTION}
                                onValueChange={value =>
                                    setNewFolder(prev => ({
                                        ...prev,
                                        parentUid: value === NONE_OPTION || value === '' ? '' : value,
                                    }))
                                }
                                placeholder={t('folders.select_parent_placeholder')}
                                searchPlaceholder={t('folders.editor.search_placeholder')}
                                emptyMessage={t('folders.editor.no_results')}
                                data-testid="create-folder-parent"
                            />
                        </div>

                        <div>
                            <Label>{t('folders.create_dialog.document_type_label')}</Label>
                            <SearchableSelect
                                options={createDocumentTypeOptions}
                                value={newFolder.documentTypeUid || NONE_OPTION}
                                onValueChange={value =>
                                    setNewFolder(prev => ({
                                        ...prev,
                                        documentTypeUid: value === NONE_OPTION || value === '' ? '' : value,
                                    }))
                                }
                                placeholder={t('folders.select_document_type_placeholder')}
                                searchPlaceholder={t('folders.editor.search_placeholder')}
                                emptyMessage={t('folders.editor.no_results')}
                                data-testid="create-folder-document-type"
                            />
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setIsCreateDialogOpen(false)}
                                disabled={createFolderMutation.isPending}
                                data-testid="create-folder-cancel"
                            >
                                {t('folders.create_dialog.cancel')}
                            </Button>
                            <Button
                                onClick={submitCreate}
                                disabled={
                                    createFolderMutation.isPending ||
                                    (!newFolder.name.en && !newFolder.name.fr)
                                }
                                data-testid="create-folder-submit"
                            >
                                {createFolderMutation.isPending
                                    ? t('folders.create_dialog.creating')
                                    : t('folders.create_dialog.submit')}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {layout?.code && (
                <FolderLayoutImportDialog
                    org={org}
                    open={isImportDialogOpen}
                    onOpenChange={setIsImportDialogOpen}
                    lockedLayoutCode={layout.code}
                    onImported={result => {
                        setImportNotice(
                            t('import.notice_success', {
                                layoutsCreated: result.layouts_created,
                                layoutsUpdated: result.layouts_updated,
                                foldersCreated: result.folders_created,
                                foldersUpdated: result.folders_updated,
                            })
                        )
                        invalidateFolders()
                        void refetchLayout()
                    }}
                />
            )}
        </div>
    )
}

// Re-exported for tests / future consumers that need the cycle guard.
export { subtreeUids }
